/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { CheckCircle, Circle, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

const C = {
  cream: '#F7F6EF', charcoal: '#2F3A42', navy: '#445366',
  mid: '#7A8A96', border: '#D8DEE3',
  skyBlue: '#BCD9E5', skyBlueLight: '#EAF4F8', skyText: '#3A8FA3',
  green: '#7EA594', greenLight: '#EAF2EF',
  orange: '#E4AC49', orangeLight: '#FDF3DC',
  red: '#DC3545', redLight: '#FFF0F0',
  white: '#FFFFFF', yellow: '#F7EC71',
};

const ROLE_COLOUR = {
  Server:  { bg: '#EDF3FC', text: '#2563EB', border: '#BAD4F5' },
  Barista: { bg: C.orangeLight, text: C.orange, border: '#F5D9A0' },
  Kitchen: { bg: C.greenLight, text: C.green, border: '#B5D4C8' },
};

// ── Section accordion ────────────────────────────────────────────────
function Section({ section, index, total }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div style={{
      border: `1px solid ${C.border}`,
      borderRadius: '12px',
      overflow: 'hidden',
      background: C.white,
      marginBottom: '10px',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '16px 20px',
          background: 'none', border: 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{
            width: '24px', height: '24px', borderRadius: '50%',
            background: C.navy, color: C.white,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '11px', fontWeight: 700, flexShrink: 0,
          }}>{index + 1}</span>
          <span style={{ fontWeight: 600, fontSize: '14px', color: C.charcoal }}>
            {section.title}
          </span>
        </div>
        {open
          ? <ChevronUp size={16} color={C.mid} />
          : <ChevronDown size={16} color={C.mid} />}
      </button>
      {open && (
        <div style={{
          padding: '4px 20px 20px 20px',
          borderTop: `1px solid ${C.border}`,
        }}>
          <div style={{
            fontSize: '13px', lineHeight: '1.75', color: C.charcoal,
            whiteSpace: 'pre-line', paddingTop: '14px',
          }}>
            {section.body}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────
export default function Onboarding() {
  const token = window.location.pathname.split('/welcome/')[1]?.split('/')[0];

  const [invite, setInvite]     = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]         = useState(false);

  useEffect(() => {
    if (!token) { setError('Invalid link.'); setLoading(false); return; }
    load();
  }, [token]);

  async function load() {
    setLoading(true);
    // fetch invite
    const { data: inv, error: invErr } = await supabase
      .from('onboarding_invites')
      .select('*')
      .eq('token', token)
      .eq('type', 'pack')
      .single();

    if (invErr || !inv) { setError('This link is invalid or has expired.'); setLoading(false); return; }

    // check expiry
    if (inv.expires_at && new Date(inv.expires_at) < new Date()) {
      setError('This link has expired. Please contact your manager for a new one.');
      setLoading(false); return;
    }

    setInvite(inv);

    // if already completed
    if (inv.completed_at) { setDone(true); setLoading(false); return; }

    // mark as opened if first time
    if (!inv.opened_at) {
      await supabase
        .from('onboarding_invites')
        .update({ opened_at: new Date().toISOString() })
        .eq('token', token);
    }

    // fetch template
    const { data: tmpl, error: tmplErr } = await supabase
      .from('onboarding_templates')
      .select('*')
      .eq('role', inv.role)
      .single();

    if (tmplErr || !tmpl) { setError('Could not load onboarding content. Please try again.'); setLoading(false); return; }
    setTemplate(tmpl);
    setLoading(false);
  }

  async function handleConfirm() {
    if (!confirmed) return;
    setSubmitting(true);
    const { error: upErr } = await supabase
      .from('onboarding_invites')
      .update({ completed_at: new Date().toISOString() })
      .eq('token', token);
    if (upErr) { setSubmitting(false); return; }
    setDone(true);
    setSubmitting(false);
  }

  // ── Loading ──
  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.mid, fontSize: '14px' }}>Loading your onboarding pack…</div>
    </div>
  );

  // ── Error ──
  if (error) return (
    <div style={{ minHeight: '100vh', background: C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '32px', maxWidth: '420px', width: '100%', textAlign: 'center' }}>
        <AlertTriangle size={32} color={C.orange} style={{ marginBottom: '12px' }} />
        <div style={{ fontWeight: 700, fontSize: '15px', color: C.charcoal, marginBottom: '8px' }}>Something went wrong</div>
        <div style={{ fontSize: '13px', color: C.mid }}>{error}</div>
      </div>
    </div>
  );

  const roleStyle = ROLE_COLOUR[invite?.role] || { bg: C.cream, text: C.navy, border: C.border };
  const sections  = template?.sections || [];

  // ── Done / already completed ──
  if (done) return (
    <div style={{ minHeight: '100vh', background: C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '40px 32px', maxWidth: '440px', width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
        {/* Logo placeholder */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: C.greenLight, border: `2px solid ${C.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
            <CheckCircle size={28} color={C.green} />
          </div>
        </div>
        <div style={{ fontWeight: 700, fontSize: '18px', color: C.charcoal, marginBottom: '8px' }}>
          All done, {invite?.name?.split(' ')[0]}!
        </div>
        <div style={{ fontSize: '13px', color: C.mid, lineHeight: '1.6' }}>
          You've confirmed your onboarding pack. Keep it bookmarked — you can refer back to it any time during your first 4 weeks.
        </div>
        <div style={{ marginTop: '24px', padding: '14px 16px', background: C.cream, borderRadius: '10px', border: `1px solid ${C.border}`, fontSize: '12px', color: C.mid }}>
          Your manager has been notified. See you on shift! 👋
        </div>
      </div>
    </div>
  );

  // ── Main pack ──
  return (
    <div style={{ minHeight: '100vh', background: C.cream }}>
      {/* Header */}
      <div style={{ background: C.navy, padding: '20px 24px' }}>
        <div style={{ maxWidth: '680px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '16px', color: C.white }}>Box Sociaal</div>
            <div style={{ fontSize: '12px', color: C.skyBlue, marginTop: '2px' }}>Onboarding Pack</div>
          </div>
          <span style={{
            padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
            background: roleStyle.bg, color: roleStyle.text, border: `1px solid ${roleStyle.border}`,
          }}>
            {invite?.role}
          </span>
        </div>
      </div>

      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '24px 16px 48px' }}>

        {/* Welcome banner */}
        <div style={{
          background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`,
          padding: '22px 24px', marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
        }}>
          <div style={{ fontSize: '13px', color: C.mid, marginBottom: '4px' }}>Welcome to the team,</div>
          <div style={{ fontWeight: 700, fontSize: '20px', color: C.charcoal, marginBottom: '12px' }}>{invite?.name?.split(' ')[0]} 👋</div>
          <div style={{ fontSize: '13px', color: C.mid, lineHeight: '1.6' }}>
            Please read through all sections below carefully. Once you've read and understood everything, confirm at the bottom. This is a reference you can return to throughout your first 4 weeks.
          </div>
          <div style={{ marginTop: '14px', display: 'flex', gap: '16px', fontSize: '12px', color: C.mid }}>
            <span>📍 {invite?.venue}</span>
            <span>👤 {invite?.role}</span>
          </div>
        </div>

        {/* Sections */}
        <div style={{ marginBottom: '28px' }}>
          {sections.map((s, i) => (
            <Section key={s.id} section={s} index={i} total={sections.length} />
          ))}
        </div>

        {/* Confirmation */}
        <div style={{
          background: C.white, borderRadius: '14px',
          border: `2px solid ${confirmed ? C.green : C.border}`,
          padding: '24px',
          transition: 'border-color 0.2s',
        }}>
          <div style={{ fontWeight: 600, fontSize: '14px', color: C.charcoal, marginBottom: '16px' }}>
            Confirm you've read this pack
          </div>

          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: '12px',
            cursor: 'pointer', marginBottom: '20px',
          }}>
            <div
              onClick={() => setConfirmed(c => !c)}
              style={{ marginTop: '1px', flexShrink: 0, cursor: 'pointer' }}
            >
              {confirmed
                ? <CheckCircle size={22} color={C.green} />
                : <Circle size={22} color={C.border} />}
            </div>
            <span style={{ fontSize: '13px', color: C.charcoal, lineHeight: '1.6' }}>
              I, <strong>{invite?.name}</strong>, confirm that I have read and understood the Box Sociaal onboarding pack, including the House Rules & Standards. I agree to abide by these policies.
            </span>
          </label>

          <button
            onClick={handleConfirm}
            disabled={!confirmed || submitting}
            style={{
              width: '100%', padding: '13px',
              background: confirmed ? C.navy : C.border,
              color: confirmed ? C.white : C.mid,
              border: 'none', borderRadius: '10px',
              fontSize: '14px', fontWeight: 600,
              cursor: confirmed ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
            }}
          >
            {submitting ? 'Submitting…' : 'Confirm & Complete Onboarding Pack'}
          </button>

          {!confirmed && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: C.mid, textAlign: 'center' }}>
              Tick the box above to confirm you've read everything
            </div>
          )}
        </div>
      </div>
    </div>
  );
}